"use client";

import { isInLiveAppointmentWindow } from "@/lib/modules/auth/idle-logoff";
import { useEffect, useState, useSyncExternalStore } from "react";

// How many mounted components currently hold the idle logoff paused.
let pauseCount = 0;
const listeners = new Set<() => void>();

/**
 * Holds off the staff idle logoff while `isLive` is true, e.g. while a
 * therapist is in Together Mode or transcribing a session. The tab keeps
 * reporting activity for them, so the server never sees the session as idle.
 */
export function useIdleLogoffPause(isLive: boolean) {
  useEffect(() => {
    if (!isLive) {
      return;
    }
    pauseCount += 1;
    _notify();
    return () => {
      pauseCount -= 1;
      _notify();
    };
  }, [isLive]);
}

/** Pauses the idle logoff while an appointment is in its scheduled window. */
export function useIdleLogoffPauseDuringAppointment(appointment: {
  startTime: Date;
  endTime: Date;
  cancelledAt?: Date | null;
}) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);

  useIdleLogoffPause(
    !appointment.cancelledAt && isInLiveAppointmentWindow(appointment, nowMs),
  );
}

/** True while anything on the page holds the idle logoff paused. */
export function useIsIdleLogoffPaused(): boolean {
  return useSyncExternalStore(
    _subscribe,
    () => pauseCount > 0,
    () => false,
  );
}

function _subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function _notify() {
  listeners.forEach((listener) => listener());
}
